/**
 * @file booking.schema.ts
 * @path apps/website/src/features/booking/schemas/booking.schema.ts
 * @description
 * Zod schemas for each wizard step.
 * Each `validate*` function returns field-level errors with `elementId` for
 * scroll-to-error support.
 */

import { z } from "zod";
import { BookingState } from "@sirbastians/types";

// ---------------------------------------------------------------------------
// Shared result type
// ---------------------------------------------------------------------------

export interface FieldError {
  field: string;
  message: string;
  elementId: string;
}

export interface StepValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  fieldErrors: FieldError[];
}

const ok = (): StepValidationResult => ({
  isValid: true,
  errors: [],
  warnings: [],
  fieldErrors: [],
});

const err = (
  errors: string[],
  fieldErrors: FieldError[],
  warnings: string[] = [],
): StepValidationResult => ({
  isValid: errors.length === 0,
  errors,
  warnings,
  fieldErrors,
});

// ---------------------------------------------------------------------------
// Step 1 — Service Selection
// ---------------------------------------------------------------------------

export const validateServiceStep = (
  state: BookingState,
): StepValidationResult => {
  if (!state.selectedService) {
    const msg = "Choose a service to continue";
    return err(
      [msg],
      [
        {
          field: "service-selector",
          message: msg,
          elementId: "service-selector",
        },
      ],
    );
  }
  return ok();
};

// ---------------------------------------------------------------------------
// Step 2 — Trip Details
// ---------------------------------------------------------------------------

export const validateDetailsStep = (
  state: BookingState,
): StepValidationResult => {
  const errors: string[] = [];
  const fieldErrors: FieldError[] = [];

  const adultsCount = state.passengers.filter((p) => p.type === "adult").length;
  const minAdults =
    state.selectedService?.packageConfig?.passengerConfig?.minAdults ?? 1;

  if (adultsCount < minAdults) {
    const msg = `Add at least ${minAdults} adult${minAdults > 1 ? "s" : ""} to continue`;
    errors.push(msg);
    fieldErrors.push({
      field: "passengers",
      message: msg,
      elementId: "passengers",
    });
  }

  const primary = state.passengers.find((p) => p.isPrimary);
  if (!primary) {
    const msg = "Pick one person as the main contact";
    errors.push(msg);
    fieldErrors.push({
      field: "passengers",
      message: msg,
      elementId: "passengers",
    });
  } else {
    if (!primary.firstName?.trim()) {
      const msg = "First name is required";
      errors.push(msg);
      fieldErrors.push({
        field: "primaryFirstName",
        message: msg,
        elementId: "primaryFirstName",
      });
    }
    if (!primary.email?.trim()) {
      const msg = "Email address is required";
      errors.push(msg);
      fieldErrors.push({
        field: "primaryEmail",
        message: msg,
        elementId: "primaryEmail",
      });
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(primary.email)) {
      const msg = "Enter a valid email address";
      errors.push(msg);
      fieldErrors.push({
        field: "primaryEmail",
        message: msg,
        elementId: "primaryEmail",
      });
    }
    if (!primary.phone?.trim()) {
      const msg = "Phone number is required";
      errors.push(msg);
      fieldErrors.push({
        field: "primaryPhone",
        message: msg,
        elementId: "primaryPhone",
      });
    }
  }

  if (
    state.selectedService?.slug === "airport-transfers" &&
    !state.flightNumber?.trim()
  ) {
    const msg = "Enter your flight number";
    errors.push(msg);
    fieldErrors.push({
      field: "flightNumber",
      message: msg,
      elementId: "flightNumber",
    });
  }

  if (
    state.selectedService?.slug === "private-vehicle-hire" &&
    !state.duration
  ) {
    const msg = "Select a trip duration (half-day or full-day)";
    errors.push(msg);
    fieldErrors.push({
      field: "duration-selector",
      message: msg,
      elementId: "duration-selector",
    });
  }

  if (state.selectedVehicles.length === 0) {
    const msg = "Pick at least one vehicle";
    errors.push(msg);
    fieldErrors.push({
      field: "vehicle-selector",
      message: msg,
      elementId: "vehicle-selector",
    });
  } else {
    let totalCapacity = 0;
    for (const v of state.selectedVehicles) {
      if (v.type === "standard") {
        totalCapacity +=
          (state.selectedService?.standardVehicle?.maxPassengers ?? 0) *
          v.count;
      } else {
        const vip = state.selectedService?.vipOptions?.find(
          (o: { id: string }) => o.id === v.id,
        );
        if (vip) totalCapacity += (vip.maxPassengers ?? 0) * v.count;
      }
    }
    const requiredSeats =
      state.passengers.filter((p) => p.type !== "infant").length +
      (state.babySeats ?? 0);

    if (totalCapacity < requiredSeats) {
      const msg = `You need seats for ${requiredSeats} people (excluding infants). Current capacity: ${totalCapacity}.`;
      errors.push(msg);
      fieldErrors.push({
        field: "vehicle-selector",
        message: msg,
        elementId: "vehicle-selector",
      });
    }
  }

  return err(errors, fieldErrors);
};

// ---------------------------------------------------------------------------
// Step 3 — Activities (always valid, warnings only)
// ---------------------------------------------------------------------------

export const validateActivitiesStep = (
  state: BookingState,
): StepValidationResult => {
  const warnings: string[] = [];
  if (
    state.includedActivities.length === 0 &&
    state.selectedActivities.length === 0
  ) {
    warnings.push("Activities are optional — you can skip this step");
  }
  return { isValid: true, errors: [], warnings, fieldErrors: [] };
};

// ---------------------------------------------------------------------------
// Step 4 — Schedule & Route
// ---------------------------------------------------------------------------

const stopAddressSchema = z.string().min(1, "Address is required");

export const validateScheduleStep = (
  state: BookingState,
): StepValidationResult => {
  const errors: string[] = [];
  const fieldErrors: FieldError[] = [];

  if (!state.selectedDate) {
    errors.push("Please select a date");
    fieldErrors.push({
      field: "date-selector",
      message: "Date is required",
      elementId: "date-selector",
    });
  }

  if (!state.selectedTime) {
    errors.push("Please select a time");
    fieldErrors.push({
      field: "startTime",
      message: "Time is required",
      elementId: "startTime",
    });
  }

  for (const stop of state.routeStops) {
    const result = stopAddressSchema.safeParse(stop.address);
    if (!result.success) {
      const msg = `${stop.name || "Stop"} needs an address`;
      fieldErrors.push({
        field: `stop-address-${stop.id}`,
        message: msg,
        elementId: `stop-address-${stop.id}`,
      });
      if (!errors.includes("Please fill in all addresses")) {
        errors.push("Please fill in all addresses");
      }
    }
  }

  return err(errors, fieldErrors);
};

// ---------------------------------------------------------------------------
// Step 5 — Review & Pay (no validation needed)
// ---------------------------------------------------------------------------

export const validateReviewStep = (
  _state: BookingState,
): StepValidationResult => ok();

// ---------------------------------------------------------------------------
// Step ID → validator map
// ---------------------------------------------------------------------------

export type StepId =
  | "service"
  | "trip-details"
  | "activities"
  | "schedule-route"
  | "review";

export const validateStep = (
  stepId: StepId,
  state: BookingState,
): StepValidationResult => {
  switch (stepId) {
    case "service":
      return validateServiceStep(state);
    case "trip-details":
      return validateDetailsStep(state);
    case "activities":
      return validateActivitiesStep(state);
    case "schedule-route":
      return validateScheduleStep(state);
    case "review":
      return validateReviewStep(state);
    default:
      return ok();
  }
};
